import React, { useState } from 'react'
import { Button, Card, Modal, Result, Space, Table } from 'antd'
import { PATTERN_TYPES, PATTERN_TYPES_VALUE_TO_NAME } from '../../../../constants/pattern-types'
import { CopyOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { actions } from '../../../../store/reducers/assignment.reducer'
import { copyToClipboard } from '../../../../utils/link.helper'
import { useParams } from 'react-router'
import { useDispatch } from 'react-redux'
import { useFetchLayerPatternsQuery, useReorderPatternsMutation } from '../../../../store/apis/pattern.api'
import { DragDropContext } from 'react-beautiful-dnd'
import PatternTableWrapper from './PatternTableWrapper'
import PatternTableRow from './PatternTableRow'
import { openNotificationWithIcon } from '../../../../utils/notification.helper'
import Access, {
    useAssignmentAccess,
} from '../../components/access/Access'
import { AssignmentPermissions } from '../../../../constants/assignment-permissions'
import PatternIcon from '../../../../components/common/PatternIcon'

const PatternsTable = () => {
    const { token, layerId } = useParams()
    const dispatch = useDispatch()

    const {
        data: patterns,
        isFetching: isFetchingPatterns,
        error: errorFetchingPatterns,
        refetch: refetchPatterns,
    } = useFetchLayerPatternsQuery({ token, layerId })
    const [reorderPatterns, reorderPatternsResult] = useReorderPatternsMutation()
    const [newTableData, setNewTableData] = useState(null)

    const hasEditingPermission = useAssignmentAccess([AssignmentPermissions.CAN_EDIT])

    if (errorFetchingPatterns)
        return (
            <Result
                status='error'
                title='Patterns fetch failed'
                subTitle={errorFetchingPatterns}
                extra={[
                    <Button key='try' onClick={() => refetchPatterns()} loading={isFetchingPatterns}>
                        Try Again
                    </Button>,
                ]}
            />
        )

    const somePatternIsQueryType = patterns?.some(pattern => pattern.type === PATTERN_TYPES.QUERY.value)

    const tableColumns = [
        ...(hasEditingPermission ? [{ key: 'sort', width: '1%' }] : []),
        {
            title: 'ID',
            dataIndex: 'id',
            width: '1%',
            render: (id, record) => <PatternIcon id={record.id} type={record.type} colorId={record.colorId} />,
        },
        {
            title: 'Name',
            dataIndex: 'name',
            width: '10%',
        },
        {
            title: 'Type',
            dataIndex: 'type',
            width: '5%',
            render: type => PATTERN_TYPES_VALUE_TO_NAME[type],
        },
        ...(somePatternIsQueryType
            ? [
                  {
                      title: 'Query',
                      dataIndex: 'query',
                      width: '20%',
                      render: (query, record) =>
                          record.type === PATTERN_TYPES.QUERY.value ? (
                              <div title={query} style={{ maxWidth: 160, overflow: 'hidden' }}>
                                  <code style={{ whiteSpace: 'nowrap' }}>{query}</code>
                              </div>
                          ) : null,
                  },
              ]
            : []),
        {
            title: 'Action',
            width: '8%',
            render: pattern => {
                const handleDeleteClick = () => {
                    Modal.confirm({
                        title: 'Confirm deleting',
                        icon: <ExclamationCircleOutlined />,
                        content: `Once you delete the pattern, there is no going back. Please be certain.`,
                        okText: 'Delete',
                        onOk() {
                            return dispatch(actions.deletePattern(token, layerId, pattern.id))
                        },
                        okType: 'danger',
                        cancelText: 'Cancel',
                    })
                }

                return (
                    <>
                        {pattern.type === PATTERN_TYPES.QUERY.value && (
                            <Button
                                shape='circle'
                                type='text'
                                onClick={() => copyToClipboard(pattern.query)}
                                aria-label='Copy query to the clipboard'
                                title='Copy query to the clipboard'
                            >
                                <CopyOutlined className='primary-color' />
                            </Button>
                        )}
                        <Access permissions={[AssignmentPermissions.CAN_EDIT]}>
                            <Button
                                shape='circle'
                                type='text'
                                onClick={handleDeleteClick}
                                aria-label='Delete pattern'
                                title='Delete pattern'
                            >
                                <DeleteOutlined className='primary-color' />
                            </Button>
                        </Access>
                    </>
                )
            },
        },
    ]

    const tableData = patterns?.map(pattern => ({
        id: pattern.id,
        name: pattern.name,
        type: pattern.type,
        query: pattern.query,
        colorId: pattern.colorId,
    }))

    const onDragEnd = ({ destination, source, draggableId }) => {
        if (!destination) return
        if (destination.index === source.index) return

        const reorderedTableData = Array.from(newTableData || tableData)
        reorderedTableData.splice(source.index, 1) // removing the item
        reorderedTableData.splice(
            destination.index,
            0,
            (newTableData || tableData).find(data => data.id.toString() === draggableId),
        ) // pushing in the new position
        setNewTableData(reorderedTableData)
    }

    const components = hasEditingPermission
        ? {
              body: {
                  wrapper: PatternTableWrapper,
                  row: PatternTableRow,
              },
          }
        : null

    const handleSaveClick = async () => {
        const idToOrderMap = {}
        newTableData.forEach((data, index) => (idToOrderMap[data.id] = index))
        const reorderPatternsResponse = await reorderPatterns({ token, layerId, idToOrderMap })
        if (reorderPatternsResponse?.data) {
            setNewTableData(null)
            openNotificationWithIcon('success', 'Patterns order saved successfully')
        } else if (reorderPatternsResponse?.error) {
            openNotificationWithIcon('error', 'Patterns order save failed', reorderPatternsResponse.error)
        }
    }

    const actionButtons = (
        <Space size={8}>
            <Button danger onClick={() => setNewTableData(null)}>
                Revert
            </Button>
            <Button type='primary' onClick={handleSaveClick} loading={reorderPatternsResult.isLoading}>
                Save
            </Button>
        </Space>
    )

    return (
        <Card title='Patterns' extra={newTableData ? actionButtons : null}>
            <DragDropContext onDragEnd={onDragEnd}>
                <Table
                    columns={tableColumns}
                    dataSource={newTableData || tableData}
                    pagination={false}
                    loading={isFetchingPatterns}
                    scroll={{ x: true }}
                    components={components}
                    onRow={(record, index) => ({ index, record })}
                />
            </DragDropContext>
        </Card>
    )
}

export default PatternsTable
